import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Mail, Send, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export const ContactSection = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interest: "",
    message: "",
  });

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.interest) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      toast.success("Demo request submitted successfully! We'll be in touch soon.");
      setForm({ name: "", email: "", company: "", phone: "", interest: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 section-alt relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#c9a84c] font-heading font-semibold text-sm tracking-widest uppercase mb-4">
              Get In Touch
            </p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight">
              Ready to Build with{" "}
              <span className="text-gradient-gold">Sovereign AI</span>?
            </h2>
            <p className="font-body text-muted-foreground mt-6 leading-relaxed">
              Whether you're exploring AI for collections, agriculture,
              healthcare, or defense — we'd love to hear from you. Request a demo
              or partner with us.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-[#c9a84c] mt-1 shrink-0" />
                <div>
                  <p className="font-heading font-semibold text-foreground text-sm">
                    Bengaluru, India
                  </p>
                  <p className="font-body text-muted-foreground text-sm">Primary Office</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="h-5 w-5 text-[#c9a84c] mt-1 shrink-0" />
                <div>
                  <p className="font-heading font-semibold text-foreground text-sm">
                    info@ojas-ai.in
                  </p>
                  <p className="font-body text-muted-foreground text-sm">General Inquiries</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="h-5 w-5 text-[#c9a84c] mt-1 shrink-0" />
                <div>
                  <p className="font-heading font-semibold text-foreground text-sm">
                    contact@ojas-ai.in
                  </p>
                  <p className="font-body text-muted-foreground text-sm">Business & Partnerships</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <form
              onSubmit={handleSubmit}
              className="glass-card rounded-xl p-8 space-y-5"
              data-testid="demo-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground font-body text-sm">Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    className="mt-1.5 bg-background/60 border-border text-foreground placeholder:text-muted-foreground/50 focus:border-[#c9a84c]/60"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground font-body text-sm">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1.5 bg-background/60 border-border text-foreground placeholder:text-muted-foreground/50 focus:border-[#c9a84c]/60"
                    data-testid="form-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground font-body text-sm">Company *</Label>
                  <Input
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Company name"
                    className="mt-1.5 bg-background/60 border-border text-foreground placeholder:text-muted-foreground/50 focus:border-[#c9a84c]/60"
                    data-testid="form-company"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground font-body text-sm">Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 XXXXX XXXXX"
                    className="mt-1.5 bg-background/60 border-border text-foreground placeholder:text-muted-foreground/50 focus:border-[#c9a84c]/60"
                    data-testid="form-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground font-body text-sm">Interest Area *</Label>
                <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                  <SelectTrigger
                    className="mt-1.5 bg-background/60 border-border text-foreground"
                    data-testid="form-interest"
                  >
                    <SelectValue placeholder="Select your interest" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="conversational-ai">Conversational AI (Ojas Vani)</SelectItem>
                    <SelectItem value="agriculture-ai">Agriculture AI (Ojas Krishi)</SelectItem>
                    <SelectItem value="healthcare-ai">Healthcare AI (Ojas Arogya)</SelectItem>
                    <SelectItem value="defense-ai">Defense AI (Ojas Raksha)</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground font-body text-sm">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your requirements..."
                  className="mt-1.5 bg-background/60 border-border text-foreground placeholder:text-muted-foreground/50 focus:border-[#c9a84c]/60 min-h-[100px]"
                  data-testid="form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#c9a84c] text-[#0a1628] font-heading font-semibold py-6 hover:bg-[#c9a84c]/90"
                data-testid="form-submit"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {loading ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
